import { useCallback } from 'react'

import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { type POPDFLabelSetNameFormSchema } from '@webapp/modules/purchasing/components/POPDFLabelSetNameForm'
import { useMutateCreatePOPDFLabelSet } from '@webapp/modules/purchasing/features/create-po-pdf-label-set/models'
import { type PDFLabelSet } from '@webapp/modules/vendor/entities/PDFLabelSet' // eslint-disable-line import/no-internal-modules
import messages from './messages'

export interface UseCreatePOPDFLabelSetFormPresenterParams {
  onSuccess: (set: PDFLabelSet) => void
}

export const useCreatePOPDFLabelSetFormPresenter = ({
  onSuccess,
}: UseCreatePOPDFLabelSetFormPresenterParams) => {
  const intl = useProcurifyIntl()
  const { mutateAsync, isLoading } = useMutateCreatePOPDFLabelSet()

  const handleSubmit = useCallback(
    async (values: POPDFLabelSetNameFormSchema) => {
      try {
        const createdSet = await mutateAsync(values)

        toast.success({
          title: intl.formatMessage(messages.successTitle),
          children: intl.formatMessage(messages.successMessage, {
            name: values.name,
          }),
        })

        onSuccess(createdSet)
      } catch (error) {
        // API failures surface `errors.key.message` (BHV-042); the Feature
        // Set keeps the modal open because onSuccess is never called
        const errorMessage =
          getMessageFromError(error) ||
          intl.formatMessage(messages.defaultErrorMessage)

        toast.error({
          title: intl.formatMessage(messages.errorTitle),
          children: errorMessage,
        })
      }
    },
    [intl, mutateAsync, onSuccess]
  )

  return {
    states: {
      isSaving: isLoading,
    },
    handlers: {
      handleSubmit,
    },
  }
}
